/**
 * Route guard: blocks rendering of protected content until app access is verified.
 * Uses user_apps from Redux (already loaded with user) so the check is synchronous—
 * no API call, no flash of content before redirect.
 * Backend uses is_app_accessible(user, Lien) with Lifeapp.Lien; user_apps is the
 * list of allowed apps (filtered by subscription) so we check Lien against that.
 */
import { useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'

// Frontend path (or prefix) → backend Lifeapp.Lien used in is_app_accessible()
const PATH_TO_LIEN = [
  { path: '/MonPatrimoine', lien: '/domicile/monimmobilier' },
  { path: '/domicile', lien: '/domicile/monimmobilier' },
  { path: '/Dashboard', lien: '/Dashboard' },
  { path: '/documents', lien: '/documents' },
]

const DEFAULT_DENIED_MESSAGE = "Cette fonctionnalité n'est pas disponible avec votre abonnement."

function getRequiredLien(pathname) {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`
  for (const { path: prefix, lien } of PATH_TO_LIEN) {
    if (path === prefix || path.startsWith(prefix + '/')) return lien
  }
  return null
}

function hasAccessToLien(userApps, requiredLien) {
  if (!requiredLien || !Array.isArray(userApps)) return true
  const liens = userApps.map((app) => app.Lien).filter(Boolean)
  return liens.includes(requiredLien)
}

/**
 * Renders children only if the current path is allowed by user_apps (subscription).
 * Otherwise redirects to /access-denied immediately without rendering children (no flash).
 */
export function AppAccessRoute({ children, requiredLien: propLien }) {
  const location = useLocation()
  const userApps = useSelector((state) => state.user?.user?.user_apps)
  const pathname = location.pathname

  const requiredLien = propLien != null ? propLien : getRequiredLien(pathname)

  if (requiredLien == null) {
    return children
  }

  if (!userApps) {
    return null
  }

  if (!hasAccessToLien(userApps, requiredLien)) {
    const message = location.state?.denied_message || DEFAULT_DENIED_MESSAGE
    try {
      localStorage.setItem('denied_message', message)
    } catch (_) {}
    return <Navigate to="/access-denied" replace state={{ from: pathname }} />
  }

  return children
}

export default AppAccessRoute
